import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { usePlatform } from "@/context/PlatformContext";
import {
  Wallet, ArrowRight, ShieldCheck, Users2, Layers, Receipt,
  TrendingUp, Sparkles, LineChart, Lock, Github, Twitter,
} from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import PublicHeader from "@/components/PublicHeader";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const navigate = useNavigate();
  const { settings } = usePlatform();
  const { user } = useAuth();
  const isLoggedIn = Boolean(user && user.id);

  return (
    <div className="min-h-screen bg-[#fdfdfd] text-slate-900 overflow-hidden">
      <SEO
        title={`${settings?.title || "GEMS"} — Group Expense Ledger & Bill Splitter`}
        description={settings?.description || "GEMS is a modern group-expense management platform with an immutable ledger for small crews."}
        keywords="group expense manager, bill splitter, split bills, shared wallet, expense ledger, settle up, trip expenses"
        canonicalUrl="https://splitly.app/"
      />
      {/* Nav */}
      <PublicHeader />

      {/* Hero */}
      <section className="relative">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-100 blur-3xl opacity-70 pointer-events-none" />
        <div className="absolute -top-20 -left-40 w-96 h-96 rounded-full bg-rose-100 blur-3xl opacity-70 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-12 lg:pb-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs font-semibold text-slate-600 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
              Built around an immutable ledger
            </div>
            <h1 className="heading text-5xl lg:text-7xl font-bold tracking-tight mt-6 leading-[1.05]">
              {settings.tagline?.split(".")[0] || "Split expenses"}.
              <br />
              <span className="text-slate-500">
                {settings.tagline?.split(".").slice(1).join(".").trim() || "Settle up. Stay friends."}
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              {settings.description || "GEMS is a modern group-expense management platform with an immutable ledger for small crews."}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate(isLoggedIn ? "/dashboard" : "/login")}
                className="h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center gap-2 active:scale-95 transition-all"
                data-testid="landing-hero-cta"
              >
                {isLoggedIn ? "Go to Workspace" : "Get started"} <ArrowRight className="w-4 h-4" />
              </button>
              {!isLoggedIn && (
                <Link
                  to="/register"
                  className="h-12 px-5 rounded-full bg-white hover:bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-700 flex items-center gap-2"
                  data-testid="landing-hero-register"
                >
                  Create your group
                </Link>
              )}
              <div className="text-xs text-slate-500 flex items-center gap-1.5 ml-2">
                <Lock className="w-3 h-3" /> No credit card. Free while in beta.
              </div>
            </div>
          </motion.div>

          {/* Preview mock */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-14 lg:mt-20 relative"
          >
            <PreviewCard />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Why GEMS</div>
          <h2 className="heading text-3xl lg:text-4xl font-bold mt-2 tracking-tight">Everything a small group needs, nothing they don't.</h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          <Feature
            icon={ShieldCheck}
            tint="emerald"
            title="Admin-managed groups"
            body="You create the group and invite members with their own login. You choose who's in — nobody else."
          />
          <Feature
            icon={Users2}
            tint="rose"
            title="Your own group URL"
            body="Grab a slug like /weekend-friends. Share it with your crew. Everything lives at your address."
          />
          <Feature
            icon={Layers}
            tint="blue"
            title="Categories per group"
            body="Every group sets its own expense categories with custom icons and colors. Nothing bleeds across groups."
          />
          <Feature
            icon={Receipt}
            tint="orange"
            title="Equal splits by default"
            body="Pick who's in on an expense — GEMS divides it and records who owes whom automatically."
          />
          <Feature
            icon={TrendingUp}
            tint="purple"
            title="Beautiful reports"
            body="Monthly spend, category breakdown, top spender, settlement history — all in a single glance."
          />
          <Feature
            icon={LineChart}
            tint="slate"
            title="Timeline & running balance"
            body="Open any member to see the full transaction history with a running balance you can trust."
          />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-slate-50/80 border-y border-slate-200/60 py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">How it works</div>
            <h2 className="heading text-3xl lg:text-4xl font-bold mt-2 tracking-tight">From zero to your first settlement in under a minute.</h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            <Step n="01" title="Create your account" body="Sign up in seconds. No credit card." />
            <Step n="02" title="Pick a group slug" body="Your unique URL like /bali-2026 or /flat-303. Yours to keep." />
            <Step n="03" title="Add members & log expenses" body="Invite crew with their own logins. Log expenses, settle up, download reports." />
          </div>

          <div className="mt-10">
            <button
              onClick={() => navigate(isLoggedIn ? "/dashboard" : "/login")}
              className="h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center gap-2 active:scale-95"
              data-testid="landing-how-cta"
            >
              {isLoggedIn ? "Go to Workspace" : "Get started"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Ledger callout */}
      <section id="security" className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-3xl bg-slate-900 text-white p-8 lg:p-12 relative overflow-hidden grain">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center relative">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-emerald-300">
                <ShieldCheck className="w-4 h-4" />
                Immutable by design
              </div>
              <h2 className="heading text-3xl lg:text-4xl font-bold mt-3 tracking-tight">
                Every rupee traceable.
                <br />
                <span className="text-emerald-300">Nothing rewritten.</span>
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                GEMS is built on double-entry principles. Every expense and settlement
                writes an entry to an append-only ledger. Balances are derived, never
                stored — so you can trace any balance back to the exact events that
                produced it. Perfect for trust-heavy roommate and travel groups.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Ledger entries per expense" value="1 per member" />
              <MiniStat label="Historical mutations" value="Zero" />
              <MiniStat label="Balance calculation" value="On-demand" />
              <MiniStat label="Auditable trail" value="Always" />
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 pb-24 text-center">
        <h2 className="heading text-3xl lg:text-4xl font-bold tracking-tight">Ready when you are.</h2>
        <p className="mt-3 text-slate-500 max-w-md mx-auto">Grab your group slug before someone else does.</p>
        <button
          onClick={() => navigate(isLoggedIn ? "/dashboard" : "/register")}
          className="mt-6 h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2 active:scale-95"
          data-testid="landing-bottom-cta"
        >
          {isLoggedIn ? "Go to Workspace" : "Get started"} <ArrowRight className="w-4 h-4" />
        </button>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

function Feature({ icon: Icon, title, body, tint }) {
  const tints = {
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    purple: "bg-purple-50 text-purple-600",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="p-6 rounded-3xl bg-white border border-slate-200/70 card-soft hover:border-slate-300 transition-colors">
      <div className={`w-11 h-11 rounded-2xl grid place-items-center ${tints[tint]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="heading text-lg font-semibold mt-5">{title}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="p-6 rounded-3xl bg-white border border-slate-200/70">
      <div className="amount text-emerald-600 font-bold text-2xl tracking-tighter">{n}</div>
      <div className="heading text-lg font-semibold mt-4">{title}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">{label}</div>
      <div className="amount text-xl font-bold text-white mt-1.5">{value}</div>
    </div>
  );
}

function PreviewCard() {
  return (
    <div className="rounded-[36px] p-3 lg:p-4 bg-white/80 backdrop-blur border border-slate-200 card-hero mx-auto max-w-4xl">
      <div className="rounded-[28px] bg-slate-50 border border-slate-200/70 p-5 lg:p-7 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 relative overflow-hidden rounded-3xl p-6 border bg-emerald-50 border-emerald-200 grain">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Your balance</div>
            <div className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-600 text-white">COLLECT</div>
          </div>
          <div className="amount text-5xl font-bold text-emerald-700 mt-2 tracking-tight">₹1,106.44</div>
          <p className="text-xs text-slate-600 mt-1">You should collect this amount overall.</p>
          <div className="mt-5 grid grid-cols-3 gap-2">
            <MiniLight label="Expenses" value="901" />
            <MiniLight label="Total" value="₹17.05L" />
            <MiniLight label="People" value="4" />
          </div>
        </div>
        <div className="space-y-2.5">
          <PreviewRow name="Rahul" hint="you owe" amount="₹1,079.49" red />
          <PreviewRow name="Amit" hint="you owe" amount="₹108.63" red />
          <PreviewRow name="Neha" hint="owes you" amount="₹2,294.56" />
        </div>
      </div>
    </div>
  );
}

function MiniLight({ label, value }) {
  return (
    <div className="bg-white/70 backdrop-blur rounded-xl px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className="amount text-sm font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function PreviewRow({ name, hint, amount, red }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-slate-200">
      <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center heading font-semibold text-slate-500 text-sm">
        {name[0]}
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-900 text-sm">{name}</div>
        <div className={`text-[11px] font-semibold ${red ? "text-rose-600" : "text-emerald-600"}`}>{hint}</div>
      </div>
      <div className={`amount font-bold text-sm ${red ? "text-rose-600" : "text-emerald-600"}`}>{amount}</div>
    </div>
  );
}
